import React, { useEffect, useState } from "react";
import { 
  LayoutDashboard, 
  Receipt, 
  Tag, 
  Calendar, 
  Wallet,
  TrendingUp,
  Accessibility,
  Users,
  CreditCard,
  Sparkles,
} from "lucide-react";
import { whatsappApi, googleSheetsApi, creditCardSyncApi } from "@/lib/api";
import { AppLogo } from "@/components/AppLogo";
import { AI_AGENTS_TAB_ENABLED } from "@/lib/featureFlags";

// WhatsApp brand icon
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Google Sheets brand icon
const GoogleSheetsIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M19.385 2H6.615C5.17 2 4 3.17 4 4.615v14.77C4 20.83 5.17 22 6.615 22h12.77C20.83 22 22 20.83 22 19.385V4.615C22 3.17 20.83 2 19.385 2zM8 18v-2h3v2H8zm0-4v-2h3v2H8zm0-4V8h3v2H8zm8 8h-4v-2h4v2zm0-4h-4v-2h4v2zm0-4h-4V8h4v2z" fill="#0F9D58"/>
    <path d="M6.615 2C5.17 2 4 3.17 4 4.615v14.77C4 20.83 5.17 22 6.615 22h12.77C20.83 22 22 20.83 22 19.385V4.615C22 3.17 20.83 2 19.385 2H6.615zm0 1h12.77c.892 0 1.615.723 1.615 1.615v14.77c0 .892-.723 1.615-1.615 1.615H6.615C5.723 21 5 20.277 5 19.385V4.615C5 3.723 5.723 3 6.615 3z" fill="#0F9D58"/>
  </svg>
);
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";

const navigationItems = [
  {
    title: "navigation.dashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    title: "navigation.transactions",
    url: "/transactions",
    icon: Receipt,
  },
  {
    title: "navigation.categories",
    url: "/categories",
    icon: Tag,
  },
  ...(AI_AGENTS_TAB_ENABLED
    ? [
        {
          title: "navigation.aiAgents",
          url: "/ai-agents",
          icon: Sparkles,
        },
      ]
    : []),
  // TODO: Finance Agent (AI chat) nav item disabled until the feature is complete.
  // ...(AI_CHAT_TAB_ENABLED
  //   ? [
  //       {
  //         title: "navigation.aiChat",
  //         url: "/finance-agent",
  //         icon: MessageCircle,
  //         badgeNew: true as const,
  //       },
  //     ]
  //   : []),
  {
    title: "navigation.periodicPayments",
    url: "/periodic-payments",
    icon: Calendar,
  },
];

const integrationItems = [
  {
    title: "navigation.whatsappConfig",
    url: "/whatsapp-config",
    icon: WhatsAppIcon,
    iconColor: "text-[#25D366]",
    statusKey: "whatsapp" as const,
  },
  {
    title: "navigation.creditCardSync",
    url: "/integrations/credit-cards",
    icon: CreditCard,
    iconColor: "text-[#6366f1]",
    statusKey: "creditCards" as const,
  },
  {
    title: "navigation.googleSheets",
    url: "/integrations/google-sheets",
    icon: GoogleSheetsIcon,
    iconColor: "text-[#0F9D58]",
    statusKey: "googleSheets" as const,
  },
];

const settingsItems = [
  {
    title: "users.title",
    url: "/users",
    icon: Users,
  },
  {
    title: "navigation.accessibility",
    url: "/accessibility",
    icon: Accessibility,
  },
];

export function AppSidebar() {
  const { open, setOpenMobile } = useSidebar();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { creditCardSync: ccSyncEnabled } = useFeatureFlags();
  const currentPath = location.pathname;

  const visibleIntegrationItems = integrationItems.filter(
    (item) => item.statusKey !== "creditCards" || ccSyncEnabled,
  );
  
  // Integration status state
  const [integrationStatus, setIntegrationStatus] = useState<{
    whatsapp: boolean;
    googleSheets: boolean;
    creditCards: boolean;
  }>({ whatsapp: false, googleSheets: false, creditCards: false });

  // Fetch integration statuses on mount
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const [whatsappResult, googleSheetsResult, ccConnections] = await Promise.all([
          whatsappApi.verifyConnection().catch(() => ({ whatsappEnabled: false })),
          googleSheetsApi.getIntegration().catch(() => null),
          ccSyncEnabled
            ? creditCardSyncApi.getConnections().catch(() => [])
            : Promise.resolve([]),
        ]);
        
        setIntegrationStatus({
          whatsapp: whatsappResult?.whatsappEnabled ?? false,
          googleSheets: googleSheetsResult?.enabled ?? false,
          creditCards: ccConnections.some((c: any) => c.enabled),
        });
      } catch (error) {
        console.error('Failed to fetch integration statuses:', error);
      }
    };
    
    fetchStatuses();
  }, [ccSyncEnabled]);

  const isActive = (path: string) => currentPath === path;
  
  // Make transactions page wider due to table content
  const isTransactionsPage = currentPath === '/transactions';
  const sidebarWidth = isTransactionsPage ? '95vw' : '75vw';

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <>
      {isMobile && (
        <style>{`
          .mobile-sidebar-custom {
            --sidebar-width: ${sidebarWidth} !important;
          }
          .mobile-sidebar-custom [data-mobile="true"] {
            width: ${sidebarWidth} !important;
            max-width: ${sidebarWidth} !important;
            min-width: ${sidebarWidth} !important;
            right: 0 !important;
          }
          .mobile-sidebar-custom [data-sidebar="sidebar"] {
            padding: 0 !important;
            width: 100% !important;
            overflow-x: visible !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .mobile-sidebar-custom .p-4 {
            padding: 1rem !important;
          }
          .mobile-sidebar-custom [data-radix-scroll-area-viewport] {
            width: 100% !important;
          }
          .mobile-sidebar-custom .w-\\[--sidebar-width\\] {
            width: ${sidebarWidth} !important;
          }
          /* Override Sheet component directly */
          [data-mobile="true"][data-sidebar="sidebar"] {
            width: ${sidebarWidth} !important;
            max-width: ${sidebarWidth} !important;
          }
          /* Global override for this specific Sheet */
          [data-radix-dialog-content][data-mobile="true"] {
            width: ${sidebarWidth} !important;
            max-width: ${sidebarWidth} !important;
          }
        `}</style>
      )}
      <Sidebar
        side="right"
        className={`${open ? "w-64" : "w-16"} ${isMobile ? 'mobile-sidebar-custom' : ''}`}
      >
      <SidebarContent>
        <div className="p-4 border-b border-sidebar-border/90">
          {isMobile ? (
            <div className="flex items-center gap-3 justify-start">
              <AppLogo
                variant="chrome"
                frameClassName="h-9 w-9 border-sidebar-border/50"
                className="h-7 w-7 max-h-[88%] max-w-[88%]"
                alt=""
              />
              <div className="text-left">
                <h2 className="font-semibold text-sidebar-foreground tracking-tight">{t('app.title')}</h2>
                <p className="text-xs text-sidebar-foreground/65">{t('app.subtitle')}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 justify-end">
              {open && (
                <div className="text-right">
                  <h2 className="font-semibold text-sidebar-foreground tracking-tight">{t('app.title')}</h2>
                  <p className="text-xs text-sidebar-foreground/65">{t('app.subtitle')}</p>
                </div>
              )}
              <AppLogo
                variant="chrome"
                frameClassName="h-9 w-9 border-sidebar-border/50"
                className="h-7 w-7 max-h-[88%] max-w-[88%]"
                alt=""
              />
            </div>
          )}
        </div>

        <SidebarGroup>
          <SidebarGroupLabel className={`${isMobile ? "text-left" : "text-right"} uppercase tracking-[0.14em] text-[10px] text-muted-foreground`}>{t('navigation.dashboard')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end
                      onClick={handleNavClick}
                      className={({ isActive }) =>
                        `flex items-center rounded-lg ${isMobile ? 'justify-start text-left' : 'justify-end text-right'} ${
                          isActive
                            ? "bg-primary/15 text-primary font-semibold shadow-sm"
                            : "hover:bg-sidebar-accent/60 text-sidebar-foreground/90"
                        }`
                      }
                    >
                      {isMobile ? (
                        <>
                          <item.icon className="h-4 w-4 flex-shrink-0 mr-2" />
                          <span>{t(item.title)}</span>
                          {"badgeNew" in item && item.badgeNew ? (
                            <Badge variant="secondary" className="ms-1.5 shrink-0 px-1.5 py-0 text-[9px] font-bold uppercase">
                              {t("navigation.new", "New")}
                            </Badge>
                          ) : null}
                        </>
                      ) : (
                        <>
                          {open && (
                            <>
                              <span className="ml-2">{t(item.title)}</span>
                              {"badgeNew" in item && item.badgeNew ? (
                                <Badge variant="secondary" className="ms-1.5 shrink-0 px-1.5 py-0 text-[9px] font-bold uppercase">
                                  {t("navigation.new", "New")}
                                </Badge>
                              ) : null}
                            </>
                          )}
                          <item.icon className="h-4 w-4 flex-shrink-0" />
                        </>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className={`${isMobile ? "text-left" : "text-right"} uppercase tracking-[0.14em] text-[10px] text-muted-foreground`}>{t('navigation.integrations', 'Integrations')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleIntegrationItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end
                      onClick={handleNavClick}
                      className={({ isActive }) =>
                        `flex items-center rounded-lg ${isMobile ? 'justify-start text-left' : 'justify-end text-right'} ${
                          isActive
                            ? "bg-primary/15 text-primary font-semibold shadow-sm"
                            : "hover:bg-sidebar-accent/60 text-sidebar-foreground/90"
                        }`
                      }
                    >
                      {isMobile ? (
                        <>
                          <div className="relative flex-shrink-0 mr-2">
                            <item.icon className={`h-4 w-4 ${item.iconColor}`} />
                            {integrationStatus[item.statusKey] && (
                              <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-green-500 ring-1 ring-background" />
                            )}
                          </div>
                          <span>{t(item.title)}</span>
                        </>
                      ) : (
                        <>
                          {open && <span className="ml-2">{t(item.title)}</span>}
                          <div className="relative flex-shrink-0">
                            <item.icon className={`h-4 w-4 ${item.iconColor}`} />
                            {integrationStatus[item.statusKey] && (
                              <span className="absolute -top-0.5 -left-0.5 h-2 w-2 rounded-full bg-green-500 ring-1 ring-background" />
                            )}
                          </div>
                        </>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className={`${isMobile ? "text-left" : "text-right"} uppercase tracking-[0.14em] text-[10px] text-muted-foreground`}>{t('navigation.settings', 'הגדרות')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {settingsItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end
                      onClick={handleNavClick}
                      className={({ isActive }) =>
                        `flex items-center rounded-lg ${isMobile ? 'justify-start text-left' : 'justify-end text-right'} ${
                          isActive
                            ? "bg-primary/15 text-primary font-semibold shadow-sm"
                            : "hover:bg-sidebar-accent/60 text-sidebar-foreground/90"
                        }`
                      }
                    >
                      {isMobile ? (
                        <>
                          <item.icon className="h-4 w-4 flex-shrink-0 mr-2" />
                          <span>{t(item.title)}</span>
                        </>
                      ) : (
                        <>
                          {open && <span className="ml-2">{t(item.title)}</span>}
                          <item.icon className="h-4 w-4 flex-shrink-0" />
                        </>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
    </>
  );
}